// Only these operators become their own token - anything else (numbers, parens, unrecognized symbols) is left as-is.
// Mirrors the grammar Expr.fromStringFormula actually supports on the backend (which also allows TRUE/FALSE
// literals, just not worth their own palette chips).
export const TIER_FORMULA_OPERATORS = [
  '=',
  '!=',
  '>',
  '>=',
  '<',
  '<=',
  'AND',
  'OR',
  '+',
  '-',
  '*',
  '/',
  '(',
  ')',
  'MAX',
  'MIN',
];

const escapeRegExp = (value) => value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

// Longest operators first so ">=" isn't matched as ">" followed by leftover "="
const OPERATOR_PATTERN = TIER_FORMULA_OPERATORS.slice()
  .sort((a, b) => b.length - a.length)
  .map(escapeRegExp)
  .join('|');

// Matches variable references and known operators; anything else (numbers, disallowed symbols) is left as text
const TOKEN_PATTERN = new RegExp(`\\[[^\\]]+\\]|${OPERATOR_PATTERN}`, 'gi');

let tokenIdCounter = 0;
const nextTokenId = () => `tok-${++tokenIdCounter}`;

export const newVariableToken = (variable) => ({
  id: nextTokenId(),
  type: 'variable',
  code: variable.code,
  displayName: variable.tokenLabel ?? variable.displayName,
});

export const newOperatorToken = (value) => ({
  id: nextTokenId(),
  type: 'operator',
  value,
});

export const newTextToken = (value) => ({
  id: nextTokenId(),
  type: 'text',
  value,
});

// Splits a raw text chunk into individual text tokens, treating parens and commas as their own tokens so one
// touching a number or variable (ex: "70000)" or ",500") doesn't get glued into it as one unparseable blob
const pushTextTokens = (tokens, text) => {
  for (const part of text.match(/[(),]|[^(),\s]+/g) ?? []) {
    tokens.push(newTextToken(part));
  }
};

// Parses a raw backend formula (ex: "[CODE] > 70 AND [CODE2] = 1") into editable tokens: only variable references
// and known operators become their own token, everything else is kept as-is in a single text token
export const tokenizeFormula = (formula, codeToVariable = {}) => {
  if (!formula) return [];

  const tokens = [];
  let lastIndex = 0;

  for (const match of formula.matchAll(TOKEN_PATTERN)) {
    pushTextTokens(tokens, formula.slice(lastIndex, match.index));

    if (match[0].startsWith('[')) {
      const code = match[0].slice(1, -1);
      const variable = codeToVariable[code];
      tokens.push({
        id: nextTokenId(),
        type: 'variable',
        code,
        displayName: variable?.tokenLabel ?? variable?.displayName,
      });
    } else {
      tokens.push({ id: nextTokenId(), type: 'operator', value: match[0].toUpperCase() });
    }

    lastIndex = match.index + match[0].length;
  }

  pushTextTokens(tokens, formula.slice(lastIndex));

  return tokens;
};

export const serializeTokens = (tokens) =>
  tokens.map((token) => (token.type === 'variable' ? `[${token.code}]` : token.value)).join(' ');

// Renders a raw formula (ex: "[NET_SALES] >= 500") for read-only display exactly as the builder would show it:
// variables by display name, numbers formatted by what they're compared against (ex: "Net Sales >= $500.00")
export const formatFormulaDisplay = (formula, codeToVariable = {}, defaultFormat) => {
  const tokens = tokenizeFormula(formula, codeToVariable);
  return tokens.map((_token, index) => displayValueToken(tokens, index, codeToVariable, defaultFormat)).join(' ');
};

const NUMERIC_VALUE_PATTERN = /^-?(\d+(\.\d+)?|\.\d+)$/;

// Cosmetic display/input formatting for Tier formula values, keyed by "format" (ex: CURRENCY, PERCENT) that
// useTierFormulaPalette attaches to each variable descriptor based on its TierVar. Purely presentational - the
// raw formula/serialized value is always the plain number `parse` produces, never the `format`-ted string.
const VALUE_FIELD_FORMATS = {
  CURRENCY: {
    format: (value) => new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(value),
    // Only worth parsing if it actually looks like typed currency (ex: "$70,000.00"); a plain "70000" is passed through
    parse: (value) => {
      if (!value.includes('$') && !value.includes(',')) return null;
      const match = value.match(/^\$?(-?\d{1,3}(?:,\d{3})*(?:\.\d+)?|-?\d+(?:\.\d+)?)$/);
      return match ? match[1].replace(/,/g, '') : null;
    },
  },
  PERCENT: {
    format: (value) => new Intl.NumberFormat('en-US', { style: 'percent' }).format(value),
    parse: (value) => {
      const match = value.match(/^(-?\d+(?:\.\d+)?)%$/);
      return match ? String(Number(match[1]) / 100) : null;
    },
  },
};

const LOGICAL_OPERATORS = new Set(['AND', 'OR']);
const COMPARISON_OPERATORS = new Set(['=', '!=', '>', '<', '>=', '<=']);

// Finds the comparison operator (=, >, <, ...) that governs the value at `index`, searching outward from it and
// respecting parens (ex: "(" / ")" text tokens) so grouped expressions like "(A + B) = 70" aren't mistaken for two
// separate expressions. Leaving the enclosing group keeps searching in the parent (ex: the 500 in
// "A >= MAX(500, B)" is governed by ">="); crossing an AND/OR before finding one stops.
const findGoverningComparison = (tokens, index) => {
  for (const step of [1, -1]) {
    let depth = 0;
    for (let i = index; i >= 0 && i < tokens.length; i += step) {
      const token = tokens[i];
      const isOpen = (token.type === 'text' || token.type === 'operator') && token.value === (step === 1 ? '(' : ')');
      const isClose = (token.type === 'text' || token.type === 'operator') && token.value === (step === 1 ? ')' : '(');
      if (isOpen) depth++;
      else if (isClose) {
        depth = Math.max(depth - 1, 0);
      } else if (depth === 0 && token.type === 'operator') {
        if (LOGICAL_OPERATORS.has(token.value)) break;
        if (COMPARISON_OPERATORS.has(token.value)) return { index: i, side: step === 1 ? 'right' : 'left' };
      }
    }
  }
  return null;
};

// A value token's variable is whichever variable sits on the other side of its governing comparison operator,
// possibly nested in parens/arithmetic (ex: "(A + B) = 70" or "70 <= [CODE]")
const adjacentVariableFormat = (tokens, index, codeToVariable) => {
  const comparison = findGoverningComparison(tokens, index);
  if (!comparison) return null;

  const step = comparison.side === 'right' ? 1 : -1;
  for (let i = comparison.index + step; i >= 0 && i < tokens.length; i += step) {
    const token = tokens[i];
    if (token.type === 'operator' && LOGICAL_OPERATORS.has(token.value)) break;
    if (token.type === 'variable') return codeToVariable[token.code]?.format;
  }
  return null;
};

// A variable token displays its display name (falling back to code); a numeric value token is cosmetically
// formatted (ex: "0.7" -> "70%") based on the variable it's compared against, else `defaultFormat` (ex: a
// Threshold formula whose values are implicitly in the Tier's payment basis) - the underlying formula/serialized
// value is left untouched, this only affects the builder's rendering
export const displayValueToken = (tokens, index, codeToVariable, defaultFormat) => {
  const token = tokens[index];
  if (token.type === 'variable') return token.displayName || token.code;
  if (token.type !== 'text' || !NUMERIC_VALUE_PATTERN.test(token.value)) return token.value;

  const format = adjacentVariableFormat(tokens, index, codeToVariable) ?? defaultFormat;
  return VALUE_FIELD_FORMATS[format]?.format(Number(token.value)) ?? token.value;
};

// Lets the user type a value the same way it's displayed (ex: "70%" or "$70,000" against a LOYALTY_PCT/NET_SALES
// variable) while still storing/serializing the raw formula value ("0.7"/"70000") it represents
export const normalizeValueToken = (tokens, index, codeToVariable, defaultFormat) => {
  const token = tokens[index];
  if (token.type !== 'text') return token;

  const format = adjacentVariableFormat(tokens, index, codeToVariable) ?? defaultFormat;
  const parsed = VALUE_FIELD_FORMATS[format]?.parse(token.value);
  return parsed == null ? token : { ...token, value: parsed };
};

// The palette only surfaces the most common operators, but formulas are free to also use basic arithmetic - these
// still need a value on either side of them just like a comparison or logical operator does. No "^" here: the
// backend Expr tokenizer has no exponent support at all, so a formula using it would fail to parse on save.
const ARITHMETIC_OPERATORS = new Set(['+', '-', '*', '/']);
const ARITHMETIC_PATTERN = [...ARITHMETIC_OPERATORS].map(escapeRegExp).join('|');

// Mirrors Expr.fromStringFormula's MAX/MIN(arg, arg, ...) function-call support on the backend
const FUNCTION_NAMES = new Set(['MAX', 'MIN']);
const FUNCTION_PATTERN = [...FUNCTION_NAMES].join('|');

// A "-" attached directly to a number (ex: "-5") is a negative literal, not a subtraction operator
const NUMBER_PATTERN = '-?(?:\\d+(?:\\.\\d+)?|\\.\\d+)';

// Splits a raw formula into variable references, numbers, function names, parens/commas, and operators; anything
// left over is a generic "value" (ex: TRUE/FALSE literals). Function names use a word boundary so they aren't
// matched inside longer identifiers.
const VALIDATION_TOKEN_PATTERN = new RegExp(
  `\\[[^\\]]+\\]|${NUMBER_PATTERN}|\\b(?:${FUNCTION_PATTERN})\\b|${OPERATOR_PATTERN}|${ARITHMETIC_PATTERN}|[(),]|\\S+`,
  'gi',
);

const classifyValidationToken = (raw) => {
  if (raw === '(') return 'open';
  if (raw === ')') return 'close';
  if (raw === ',') return 'comma';
  if (raw.startsWith('[')) return 'value';
  if (FUNCTION_NAMES.has(raw.toUpperCase())) return 'function';
  if (LOGICAL_OPERATORS.has(raw.toUpperCase())) return 'logical';
  if (COMPARISON_OPERATORS.has(raw)) return 'comparison';
  if (ARITHMETIC_OPERATORS.has(raw)) return 'operator';
  return 'value';
};

// Walks the formula's tokens left to right, checking that operators/parens/values alternate the way a valid
// boolean expression would (ex: two operators or two values can't sit next to each other, parens must balance)
export const validateFormula = (formula) => {
  if (!formula || !formula.trim()) return null;

  const rawTokens = formula.match(VALIDATION_TOKEN_PATTERN) ?? [];
  const isOperatorType = (type) => type === 'logical' || type === 'comparison' || type === 'operator';

  let depth = 0;
  let prevType = null; // null means "start of formula"

  for (const raw of rawTokens) {
    const type = classifyValidationToken(raw);

    if (type === 'open') {
      if (prevType === 'value' || prevType === 'close') return `"${raw}" is missing an operator before it`;
      depth++;
    } else if (type === 'close') {
      depth--;
      if (depth < 0) return `"${raw}" has no matching opening parenthesis`;
      if (prevType === null || prevType === 'open' || isOperatorType(prevType)) {
        return `"${raw}" cannot immediately follow an operator or opening parenthesis`;
      }
    } else if (type === 'function') {
      // A function name behaves like a value token until its own "(" is reached below
      if (prevType === 'value' || prevType === 'close') return `"${raw}" is missing an operator before it`;
    } else if (type === 'comma') {
      if (prevType === null || prevType === 'open' || isOperatorType(prevType) || prevType === 'comma') {
        return `"${raw}" is missing a value before it`;
      }
    } else if (isOperatorType(type)) {
      if (prevType === null || prevType === 'open' || isOperatorType(prevType)) {
        return `"${raw}" is missing a value before it`;
      }
    } else if (prevType === 'value' || prevType === 'close') {
      return `"${raw}" is missing an operator before it`;
    }

    prevType = type;
  }

  if (depth > 0) return 'Formula is missing a closing parenthesis';
  if (isOperatorType(prevType) || prevType === 'open') {
    return 'Formula cannot end with an operator or opening parenthesis';
  }

  return null;
};
